import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import './MonitorBaas.css';
import './ConformidadeConfig.css';

/**
 * ConformidadeConfig.js — Fase 2 (escada de cobrança)
 * ---------------------------------------------------------------------------
 * Tela onde admin/admin_geral configuram a escada que cobra prestação de contas
 * de quem usa o cartão e não justifica:
 *
 *   GET /api/baas/account/conciliacao/config   → configuração atual
 *   PUT /api/baas/account/conciliacao/config   → salvar
 *   GET /api/baas/account/conciliacao/eventos   → timeline de auditoria
 *
 * TUDO nasce DESLIGADO. Nada dispara — nem push, nem bloqueio — até o admin
 * ligar aqui, conscientemente. O bloqueio automático é ainda mais explícito:
 * interruptor próprio, com aviso, e só vale em produção (garantido no backend).
 *
 * `onVoltar`: callback opcional chamado ao clicar em "← Voltar" no header.
 * Quem monta a rota (BaasModule) passa a navegação de volta pra Conciliação.
 *
 * LAYOUT: duas colunas em telas largas — configuração à esquerda, timeline de
 * auditoria como painel fixo à direita (estilo "settings + activity feed").
 * Escada N1→N2→N3 fica horizontal (setas → entre os níveis) para aproveitar
 * a largura; empilha de novo (setas ↓) abaixo de 980px.
 *
 * Reusa o design system do MonitorBaas (mb-*) + o próprio (cconf-*).
 */

const DEFAULTS = {
  escada_ativa: 0,
  bloqueio_automatico: 0,
  dias_nivel1: 3,
  dias_nivel2: 7,
  dias_nivel3: 14,
  teto_valor_reais: null,
  cooldown_horas: 20,
  notificar_admin: 1,
};

const envLabelOf = (env) =>
  ({ production: 'PRODUÇÃO', sandbox: 'SANDBOX' }[env] || String(env || '').toUpperCase() || '—');

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fmtMoney = (v) =>
  v == null ? null : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

// Metadados visuais de cada tipo de evento da escada.
const EVENTO = {
  lembrete_n1: { label: 'Lembrete enviado', cls: 'is-suggest', icon: '1' },
  cobranca_n2: { label: 'Cobrança enviada', cls: 'is-warn', icon: '2' },
  bloqueio_n3: { label: 'Cartão bloqueado', cls: 'is-danger', icon: '3' },
  sem_vinculo: { label: 'Órfã sem motorista', cls: 'is-neutral', icon: '?' },
};

// Interruptor reutilizável.
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      className={`cconf-toggle ${checked ? 'is-on' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={() => !disabled && onChange(!checked)}
      aria-pressed={checked}
      disabled={disabled}
    >
      <span className="cconf-toggle-knob" />
    </button>
  );
}

export default function ConformidadeConfig({ onVoltar } = {}) {
  const [connected, setConnected] = useState(null); // null=carregando, false=sem conta
  const [environment, setEnvironment] = useState(null);
  const [semPermissao, setSemPermissao] = useState(false);

  const [form, setForm] = useState(DEFAULTS);
  const [original, setOriginal] = useState(DEFAULTS);
  const [eventos, setEventos] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);

  const isProd = environment === 'production';
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const normalizar = (cfg) => ({
    escada_ativa: Number(cfg.escada_ativa) || 0,
    bloqueio_automatico: Number(cfg.bloqueio_automatico) || 0,
    dias_nivel1: Number(cfg.dias_nivel1) || 3,
    dias_nivel2: Number(cfg.dias_nivel2) || 7,
    dias_nivel3: Number(cfg.dias_nivel3) || 14,
    teto_valor_reais: cfg.teto_valor_reais == null ? null : Number(cfg.teto_valor_reais),
    cooldown_horas: Number(cfg.cooldown_horas) || 20,
    notificar_admin: cfg.notificar_admin == null ? 1 : Number(cfg.notificar_admin),
  });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/baas/account/conciliacao/config');
      if (!aliveRef.current) return;
      const cfg = normalizar(data.config || DEFAULTS);
      setForm(cfg);
      setOriginal(cfg);
      setEnvironment(data.environment || null);
      setConnected(true);
    } catch (e) {
      if (!aliveRef.current) return;
      if (e?.response?.status === 409) { setConnected(false); return; }
      if (e?.response?.status === 403) { setSemPermissao(true); setConnected(true); return; }
      toast.error(e?.response?.data?.error || 'Erro ao carregar a configuração.');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  const carregarEventos = useCallback(async () => {
    try {
      const { data } = await api.get('/api/baas/account/conciliacao/eventos', { params: { limit: 30 } });
      if (aliveRef.current) setEventos(Array.isArray(data?.items) ? data.items : []);
    } catch { /* timeline é acessório — silencioso */ }
  }, []);

  useEffect(() => { carregar(); carregarEventos(); }, [carregar, carregarEventos]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original]);

  const salvar = async () => {
    // Validação amigável dos dias (o backend também reforça).
    if (!(form.dias_nivel1 >= 1)) return toast.error('O prazo do Nível 1 deve ser de ao menos 1 dia.');
    if (!(form.dias_nivel2 > form.dias_nivel1)) return toast.error('O Nível 2 precisa ser maior que o Nível 1.');
    if (!(form.dias_nivel3 > form.dias_nivel2)) return toast.error('O Nível 3 precisa ser maior que o Nível 2.');

    setSaving(true);
    try {
      const { data } = await api.put('/api/baas/account/conciliacao/config', form);
      const cfg = normalizar(data.config || form);
      if (aliveRef.current) { setForm(cfg); setOriginal(cfg); }
      toast.success('Configuração salva.');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro ao salvar a configuração.');
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  };

  // Cabeçalho com o link de volta, repetido em todo estado (loading, erro, ok).
  const Voltar = () => (
    onVoltar ? (
      <button type="button" className="cconf-back" onClick={onVoltar}>
        ← Voltar para Conciliação
      </button>
    ) : null
  );

  // ── Estados de borda ──────────────────────────────────────────────────────
  if (connected === null) {
    return (
      <div className="mb-page">
        <div className="cconf-shell">
          <Voltar />
          <div className="mb-empty"><p>Carregando…</p></div>
        </div>
      </div>
    );
  }
  if (connected === false) {
    return (
      <div className="mb-page">
        <div className="cconf-shell">
          <Voltar />
          <div className="mb-empty">
            <span className="mb-empty-icon">🔌</span>
            <p>Nenhuma conta Stark Bank conectada.</p>
            <small>Conecte a conta para configurar a cobrança de conformidade.</small>
          </div>
        </div>
      </div>
    );
  }
  if (semPermissao) {
    return (
      <div className="mb-page">
        <div className="cconf-shell">
          <Voltar />
          <div className="mb-empty">
            <span className="mb-empty-icon">🔒</span>
            <p>Acesso restrito.</p>
            <small>Só administradores podem configurar a escada de cobrança.</small>
          </div>
        </div>
      </div>
    );
  }

  const escadaOn = !!form.escada_ativa;
  const niveis = [
    { n: 1, cls: 'is-suggest', titulo: 'Lembrete', desc: 'Push amigável pro motorista regularizar.', campo: 'dias_nivel1' },
    { n: 2, cls: 'is-warn', titulo: 'Cobrança', desc: 'Push mais firme + aviso ao administrador.', campo: 'dias_nivel2' },
    { n: 3, cls: 'is-danger', titulo: 'Bloqueio', desc: 'Bloqueia os cartões do titular (se ativado).', campo: 'dias_nivel3' },
  ];

  const Timeline = (
    <section className="mb-panel cconf-timeline">
      <div className="cconf-timeline-head">
        <h3>Atividade recente</h3>
        <button className="mb-btn-ghost cconf-btn-sm" onClick={carregarEventos}>Atualizar</button>
      </div>
      {eventos.length === 0 ? (
        <div className="mb-empty">
          <span className="mb-empty-icon">🕊️</span>
          <p>Nenhuma cobrança registrada.</p>
          <small>Quando a escada agir, os lembretes, cobranças e bloqueios aparecem aqui.</small>
        </div>
      ) : (
        <div className="cconf-events">
          {eventos.map((ev) => {
            const meta = EVENTO[ev.tipo] || { label: ev.tipo, cls: 'is-neutral', icon: '•' };
            const val = fmtMoney(ev.valor);
            return (
              <div key={ev.id} className="cconf-event">
                <div className={`cconf-event-badge ${meta.cls}`}>{meta.icon}</div>
                <div className="cconf-event-body">
                  <div className="cconf-event-title">
                    {meta.label}
                    {ev.motorista_nome && <span className="cconf-event-who"> · {ev.motorista_nome}</span>}
                    {val && <span className="cconf-event-val"> · {val}</span>}
                  </div>
                  {ev.detalhe && <div className="cconf-event-detail">{ev.detalhe}</div>}
                </div>
                <div className="cconf-event-time">{fmtDateTime(ev.criado_em)}</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );

  return (
    <div className="mb-page">
      <div className="cconf-shell">
        <Voltar />

        {/* ── HEADER ──────────────────────────────────────────────────────── */}
        <div className="mb-header">
          <div>
            <h2>
              Cobrança de Conformidade
              {environment && (
                <span className={`mb-env-badge ${isProd ? 'is-prod' : 'is-sbx'}`}>● {envLabelOf(environment)}</span>
              )}
            </h2>
            <p>Alertas escalonados para quem gasta no cartão e não presta contas.</p>
          </div>
        </div>

        {loading ? (
          <div className="mb-panel"><div className="mb-empty"><p>Carregando…</p></div></div>
        ) : (
          <div className="cconf-grid">
            {/* ── COLUNA PRINCIPAL: configuração ───────────────────────────── */}
            <div className="cconf-main">
              {/* ── INTERRUPTOR MESTRE ────────────────────────────────────── */}
              <div className={`cconf-master ${escadaOn ? 'is-on' : ''}`}>
                <div className="cconf-master-text">
                  <strong>Escada de cobrança</strong>
                  <span>
                    {escadaOn
                      ? 'Ativa: a plataforma cobra automaticamente as compras sem prestação de contas.'
                      : 'Desligada: nenhuma cobrança automática é enviada.'}
                  </span>
                </div>
                <Toggle checked={escadaOn} onChange={(v) => set({ escada_ativa: v ? 1 : 0 })} />
              </div>

              {/* ── ESCADA (horizontal em telas largas) ──────────────────── */}
              <div className={`cconf-ladder ${escadaOn ? '' : 'is-dim'}`}>
                {niveis.map((nv, i) => (
                  <React.Fragment key={nv.n}>
                    <div className={`cconf-step ${nv.cls}`}>
                      <div className="cconf-step-head">
                        <div className="cconf-step-badge">{nv.n}</div>
                        <div>
                          <strong>{nv.titulo}</strong>
                          {nv.n === 3 && !isProd && <span className="cconf-tag-mini">só produção</span>}
                        </div>
                      </div>
                      <p>{nv.desc}</p>
                      <div className="cconf-step-days">
                        <span>Dispara após</span>
                        <input
                          type="number"
                          min={1}
                          max={365}
                          value={form[nv.campo]}
                          disabled={!escadaOn}
                          onChange={(e) => set({ [nv.campo]: Math.max(1, parseInt(e.target.value, 10) || 0) })}
                        />
                        <span>dias</span>
                      </div>

                      {/* Bloqueio automático vive no Nível 3 — ação de alta consequência. */}
                      {nv.n === 3 && (
                        <div className={`cconf-block ${form.bloqueio_automatico ? 'is-on' : ''}`}>
                          <div className="cconf-block-row">
                            <div>
                              <strong>Bloquear automaticamente</strong>
                              <span>Bloqueia todos os cartões do titular. Reversível.</span>
                            </div>
                            <Toggle
                              checked={!!form.bloqueio_automatico}
                              onChange={(v) => set({ bloqueio_automatico: v ? 1 : 0 })}
                              disabled={!escadaOn}
                            />
                          </div>
                          {!!form.bloqueio_automatico && (
                            <div className="cconf-block-warn">
                              ⚠️ Bloqueia sem intervenção humana ao atingir o Nível 3
                              {isProd ? '.' : ' — só em produção (aqui é sandbox).'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {i < niveis.length - 1 && <div className="cconf-step-arrow">→</div>}
                  </React.Fragment>
                ))}
              </div>

              {/* ── OPÇÕES (cartões lado a lado) ──────────────────────────── */}
              <div className={`cconf-options ${escadaOn ? '' : 'is-dim'}`}>
                <div className="cconf-opt">
                  <div className="cconf-opt-text">
                    <strong>Avisar administradores</strong>
                    <span>No Nível 2+, notifica os admins do tenant.</span>
                  </div>
                  <Toggle
                    checked={!!form.notificar_admin}
                    onChange={(v) => set({ notificar_admin: v ? 1 : 0 })}
                    disabled={!escadaOn}
                  />
                </div>

                <div className="cconf-opt">
                  <div className="cconf-opt-text">
                    <strong>Intervalo entre lembretes</strong>
                    <span>Tempo mínimo antes de reenviar ao mesmo motorista.</span>
                  </div>
                  <div className="cconf-opt-input">
                    <input
                      type="number"
                      min={1}
                      max={168}
                      value={form.cooldown_horas}
                      disabled={!escadaOn}
                      onChange={(e) => set({ cooldown_horas: Math.max(1, parseInt(e.target.value, 10) || 0) })}
                    />
                    <span>horas</span>
                  </div>
                </div>

                <div className="cconf-opt">
                  <div className="cconf-opt-text">
                    <strong>Teto de valor</strong>
                    <span>Órfã pula direto pro bloqueio acima deste valor. Vazio desliga.</span>
                  </div>
                  <div className="cconf-opt-input">
                    <span>R$</span>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="—"
                      value={form.teto_valor_reais ?? ''}
                      disabled={!escadaOn}
                      onChange={(e) => {
                        const v = e.target.value;
                        set({ teto_valor_reais: v === '' ? null : Math.max(0, parseFloat(v) || 0) });
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* ── BARRA DE SALVAR ───────────────────────────────────────── */}
              <div className="cconf-savebar">
                {dirty && <span className="cconf-dirty">Alterações não salvas</span>}
                <button className="mb-btn-ghost" onClick={carregar} disabled={saving || !dirty}>Descartar</button>
                <button className="mb-btn-primary" onClick={salvar} disabled={saving || !dirty}>
                  {saving ? 'Salvando…' : 'Salvar configuração'}
                </button>
              </div>

              {/* Timeline entra aqui embaixo em telas estreitas (a coluna lateral some). */}
              <div className="cconf-timeline-mobile">{Timeline}</div>
            </div>

            {/* ── COLUNA LATERAL: timeline de auditoria ────────────────────── */}
            <aside className="cconf-side">
              {Timeline}
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}